import React, { useState, useCallback } from 'react';
import InPageOverlay from 'components/InPageOverlay/InPageOverlay';
import AboutWishlist from 'components/AboutWishlist/AboutWishlist';
import numeral from 'numeral';
import { LG, MD, SM } from '@zendeskgarden/react-typography';
import Flex from 'styled-flex-component';
import { Grid, Row, Col } from '@zendeskgarden/react-grid';
import { Button } from 'theme/Button';
import {
  filter,
  map,
  startCase,
  pick,
  get,
  replace,
  toUpper,
  includes,
  split,
  slice,
  size,
  join,
  find,
  reject,
} from 'lodash';
import ImageGallery from 'components/ImageGallery/ImageGallery';
import { variables } from 'theme/variables';
import { isAdmin, isFacilitator } from 'utility/hasAccount';
import history from 'historyContainer';
import { productNominalDetailsShape } from 'propTypesObjects';
import { PropTypes } from 'prop-types';
import Icon from 'components/Icon/Icon';
import useCopy from 'use-copy';
import { useHistory } from 'react-router-dom';
import { openModal } from 'state/modals/actions';
// TODO: P2-819
// import html2canvas from 'html2canvas';
// import JsPDF from 'jspdf';
// import useLocalStorage from 'hooks/useLocalStorage';
// import { getSavedListingInterests } from 'state/wishlistInterests/actions';
import {
  AttributesCol,
  HeroWrapper,
  Description,
  AttributesTable,
  ListingsActions,
  ActionButton,
} from './ListingsHero.styles';

const { custom_blue: customBlue, color_grey_300: colorGrey300 } = variables;

function generateAttributes(data = {}) {
  const attributes = pick(data, ['manufactor', 'mill_year', 'condition_name']);
  if (get(data, 'attributes.mill_year')) {
    attributes.mill_year = data.attributes.mill_year;
  }
  if (get(data, 'attributes.manufacturer')) {
    attributes.manufacturer = data.attributes.manufacturer;
  }

  return filter(
    map(attributes, (value, key) => {
      const formatted =
        key === 'price' ? `${value} ${data.quantity_uom_name}` : value;
      return {
        title: startCase(replace(key, '_name', '')),
        value: formatted,
      };
    }),
    ({ value }) => !!value
  );
}

// TODO: P2-819
// function getPDF(fileName) {
//   const input = document.getElementById('root');
//   html2canvas(input, {
//     logging: true,
//     letterRendering: 1,
//     useCORS: true,
//     imageTimeout: 0,
//     allowTaint: true,
//   }).then((canvas) => {
//     const imgData = canvas.toDataURL('img/png');
//     const pdf = new JsPDF({ precision: 10 });
//     const imgProps = pdf.getImageProperties(imgData);
//     const pdfWidth = pdf.internal.pageSize.getWidth();
//     const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
//     pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
//     pdf.save(`${fileName}.pdf`);
//   });
// }

function ListingsHero({ productDetails, isProductPrev = false }) {
  const title = productDetails.title || productDetails.name || '';
  const description = productDetails.description || '';

  // TODO: P2-1608
  // At the time of this change, the only other locations listed in Production are 'TX, USA', which we can display in full
  const locationDescription = productDetails.location_description;
  let listingLocation;
  if (!includes(locationDescription, ',')) {
    // Allows for locations like 'Singapore'
    listingLocation = locationDescription;
  } else if (includes(locationDescription, 'Batam')) {
    // Shortens the location for 'Batam, Batam City, Riau Islands, Indonesia' per user request
    listingLocation = 'Batam, Indonesia';
  } else {
    // Only display the last two comma-separated values in the location description, to prevent displaying a street address
    const locationArr = split(locationDescription, ',');
    const broadLocation = slice(
      locationArr,
      size(locationArr) - 2,
      size(locationArr)
    );
    listingLocation = join(broadLocation, ', ');
  }

  const productImages = productDetails.images || [];
  const coverPhoto = find(productImages, 'primary');
  const remainingImages = reject(productImages, 'primary');
  const images = [];
  if (coverPhoto) {
    images.push(coverPhoto);
  }
  images.push(...remainingImages);
  const attributes = generateAttributes(productDetails);
  const pageHistory = useHistory();
  const isDashboard = includes(
    history?.location?.pathname,
    '/dashboard/listings'
  );

  return (
    <HeroWrapper isDashboard={isDashboard}>
      <Grid>
        <Row className="listingTitle">
          {!isDashboard && (
            <div
              role="button"
              tabIndex={0}
              style={{ display: 'flex', color: customBlue, width: '50px' }}
              id="backButton"
              onClick={() => pageHistory.goBack()}
              onKeyDown={() => pageHistory.goBack()}
            >
              <Icon
                fontAwesome
                icon="caret-left"
                iconPrefix="fas"
                style={{ paddingRight: '3px', color: customBlue }}
              />
              Back
            </div>
          )}

          <LG bold id="listingName" style={{ lineHeight: '40px' }} as="h1">
            {title}
          </LG>
        </Row>
        <Row className="listingDetails">
          <Col className="listingImages" lg={11} xl={6} xs={11}>
            <ImageGallery
              keyDownEvents
              images={images}
              listingRework
              isDashboard={isDashboard}
            />
          </Col>
          <Col className="listingData" lg={11} xl={6} xs={11}>
            <AttributesCol isDashboard={isDashboard}>
              <HeroDescription
                description={description}
                isDashboard={isDashboard}
              />
              <HeroAttributesTable
                listingLocation={listingLocation}
                attributes={attributes}
                productDetails={productDetails}
                isDashboard={isDashboard}
              />
              <ListingsActionButtons
                isProductPrev={isProductPrev}
                productDetails={productDetails}
                isDashboard={isDashboard}
              />
            </AttributesCol>
          </Col>
        </Row>
      </Grid>
    </HeroWrapper>
  );
}

ListingsHero.propTypes = {
  productDetails: productNominalDetailsShape,
  isProductPrev: PropTypes.bool,
};

function HeroDescription({ description, isDashboard } = {}) {
  const defaultClamp = 150;
  const [clamp, setClamp] = useState(defaultClamp);
  const isClamp = description.length > clamp;
  const descriptionTrunc = isClamp
    ? `${description.substring(0, clamp)}...`
    : description;

  return (
    <div>
      <Description className="attribute-description" isDashboard={isDashboard}>
        <SM className="Title" noMargin as="h3" bold>
          Description
        </SM>
        <SM id="completeDescription" noMargin as="h3">
          {descriptionTrunc}{' '}
          <div style={{ paddingTop: '20px', paddingBottom: '20px' }}>
            {description.length > defaultClamp && isClamp && (
              <SM
                isBold
                grey
                onClick={() => setClamp(1000000)}
                style={{
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  maxWidth: '105px',
                }}
                noMargin
                as="span"
                link
              >
                Read More{' '}
                <Icon
                  icon="icon-chevron-down"
                  fontSize="14px"
                  style={{ paddingLeft: '10px', color: customBlue }}
                />
              </SM>
            )}
            {!isClamp && description.length > defaultClamp && (
              <SM
                grey
                onClick={() => setClamp(defaultClamp)}
                style={{
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  maxWidth: '105px',
                }}
                noMargin
                as="span"
                link
              >
                Read Less{' '}
                <Icon
                  icon="icon-chevron-up"
                  fontSize="14px"
                  style={{ paddingLeft: '10px', color: customBlue }}
                />
              </SM>
            )}
          </div>
        </SM>
      </Description>
    </div>
  );
}

HeroDescription.propTypes = {
  description: PropTypes.string,
  isDashboard: PropTypes.bool,
};

function HeroAttributesTable({
  attributes,
  listingLocation,
  productDetails,
  isDashboard,
} = {}) {
  const { quantity, quantity_uom_name: quantityUomName } = productDetails;
  return (
    <div style={{ borderTop: `1px solid ${colorGrey300}` }}>
      <AttributesTable isDashboard={isDashboard}>
        <Flex
          className="attributeDataSet"
          justifyBetween
          alignCenter
          style={{ paddingTop: '20px' }}
        >
          <SM className="attributeName" tag="h2">
            Available Quantity
          </SM>
          {quantity ? (
            <MD className="attributeValue" noMargin>
              {numeral(quantity).format('0,0')}{' '}
              <MD
                noMargin
                grey
                style={{ fontSize: '11px' }}
                tag="span"
                className="attributeValue"
              >
                {' '}
                {toUpper(quantityUomName)}.
              </MD>
            </MD>
          ) : (
            <MD className="attributeValue">Not Available</MD>
          )}
        </Flex>
        {map(attributes, (attribute, i) => (
          <Flex justifyBetween key={i} className="attributeDataSet">
            <SM
              className="attributeName"
              tag="h2"
              style={{ alignSelf: 'left' }}
            >
              {attribute?.title}
            </SM>
            {attribute?.value ? (
              <MD className="attributeValue" noMargin>
                {attribute?.value}
              </MD>
            ) : (
              <MD className="attributeValue">Not Available</MD>
            )}
          </Flex>
        ))}
        {listingLocation ? (
          <Flex
            className="attributeDataSet"
            id="locationDataSet"
            justifyBetween
            style={{ paddingBottom: '40px' }}
          >
            <SM
              className="attributeName"
              tag="h2"
              style={{ alignSelf: 'left' }}
            >
              Location
            </SM>
            {listingLocation ? (
              <MD className="attributeValue" noMargin>
                {listingLocation}
              </MD>
            ) : (
              <MD className="attributeValue">Not Available</MD>
            )}
          </Flex>
        ) : null}
      </AttributesTable>
    </div>
  );
}

HeroAttributesTable.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  attributes: PropTypes.array,
  listingLocation: PropTypes.string,
  productDetails: productNominalDetailsShape,
  isDashboard: PropTypes.bool,
};

const productDetailsDefault = {};

function ListingsActionButtons({
  productDetails = productDetailsDefault,
  isProductPrev,
  isDashboard,
}) {
  // const title = productDetails.title || productDetails.name || '';
  const [inPageOverlayViewOpen, setInPageOverlay] = useState(false);
  // const fileName = replace(title, ' ', '_');
  const { id, uuid, slug } = productDetails;
  const baseUrl = window.location.origin;
  const listingLink =
    slug && uuid
      ? `${baseUrl}/l/${slug}/${uuid}/`
      : slug && id
        ? `${baseUrl}/l/${slug}/${id}/`
        : '';

  const [copied, copy, setCopied] = useCopy(listingLink);
  // TODO: P2-1570 Commented out wishlist functionality. Leaving code and styling in place in case we want to turn it back on. 11-2023
  // const [savedItems, setSavedItems] = useLocalStorage('wishListSavedItems', []);

  // const memoAddOrRemoveFromLocalStore = useCallback(
  //   (e, id) => {
  //     e.stopPropagation();

  //     if (includes(savedItems, id)) {
  //       setSavedItems((prevArr) =>
  //         filter(prevArr, (storageId) => storageId !== id)
  //       );
  //     } else {
  //       setSavedItems((prevArr) => [id, ...prevArr]);
  //     }
  //   },
  //   [setSavedItems, savedItems]
  // );

  // useEffect(() => {
  //   getSavedListingInterests(savedItems);
  // }, [savedItems]);

  // const inLocalStore = useCallback(
  //   (id) => includes(savedItems, id),
  //   [savedItems]
  // );

  const copyLink = useCallback(() => {
    copy();
    setTimeout(() => {
      setCopied(false);
    }, 2000);
  }, [copy, setCopied]);

  function manageListing(_productDetails) {
    history.push(`/dashboard/listings/${_productDetails?.id}/preview`);
  }

  return (
    <ListingsActions SM={12} size={12} md="100%">
      <Flex column>
        {!isDashboard &&
          (isFacilitator() || (isAdmin() && !isProductPrev) ? (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <Button
                id="manageListingButton"
                onClick={() => manageListing(productDetails)}
                size="large"
                spacingTop
                primary
                bold
              >
                Manage Listing
              </Button>
            </div>
          ) : (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <Button
                id="submitInquiryButton"
                onClick={(e) => {
                  e.preventDefault();
                  openModal({
                    type: 'quickInquirySupply',
                  });
                }}
                size="large"
                spacingTop
                primary
                bold
              >
                Submit Inquiry
              </Button>
            </div>
          ))}
        <div className="actionButtons">
          {/* <ActionButton>
            <Button
              className="actionButton"
              buttonStatus
              onClick={(e) =>
                memoAddOrRemoveFromLocalStore(e, productDetails?.id)
              }
              size="large"
              spacingTop
              primaryOutline
              bold
            >
              <Icon
                button
                fontAwesome
                icon="heart"
                iconPrefix={`${
                  inLocalStore(productDetails?.id) ? 'fas' : 'far'
                }`}
              />
            </Button>
            <div className="actionButtonName">
              {`${inLocalStore(productDetails?.id) ? 'Saved' : 'Save'}`}
            </div>
          </ActionButton> */}
          {listingLink && (
             <ActionButton>
               <Button
                 className="actionButton"
                 onClick={copyLink}
                 size="large"
                 spacingTop
                 primaryOutline
                 bold
               >
                 <Icon fontAwesome icon="copy" iconPrefix="far" />
               </Button>
               <div className="actionButtonName">
                 {`${copied ? 'Copied!' : 'Copy Link'}`}
               </div>
             </ActionButton>
          )}
          {/*  TODO: P2-819 Turn on if better solution
          <ActionButton>
            <Button
              className="actionButton"
              buttonStatus
              onClick={() => getPDF(fileName)}
              size="large"
              spacingTop
              primaryOutline
              bold
            >
              <Icon button icon="icon-download" iconPrefix="far" />
            </Button>
            <div className="actionButtonName">Download PDF</div>
          </ActionButton> */}
        </div>
      </Flex>
      <InPageOverlay
        disableScroll
        portal
        showClose
        onClose={() => setInPageOverlay(false)}
        open={inPageOverlayViewOpen}
      >
        <div style={{ padding: '30px' }}>
          <AboutWishlist onClose={() => setInPageOverlay(false)} />
        </div>
      </InPageOverlay>
    </ListingsActions>
  );
}

ListingsActionButtons.propTypes = {
  productDetails: productNominalDetailsShape,
  isProductPrev: PropTypes.bool,
  isDashboard: PropTypes.bool,
};

export default ListingsHero;
